/**
 * F11-like immersive mode for mobile landscape capture.
 * Requires a user gesture on most browsers. iOS Safari does not support
 * element fullscreen — users need "Add to Home Screen" for chrome-free UI.
 */

export function isFullscreenActive() {
  if (typeof document === "undefined") {
    return false;
  }

  return Boolean(
    document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
  );
}

export function supportsElementFullscreen() {
  if (typeof document === "undefined") {
    return false;
  }

  const el = document.documentElement;
  return Boolean(
    el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.webkitRequestFullScreen ||
      el.mozRequestFullScreen ||
      el.msRequestFullscreen
  );
}

export function isStandaloneDisplay() {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.navigator.standalone === true
  );
}

export async function enterImmersiveMode(element) {
  if (typeof document === "undefined") {
    return false;
  }

  if (isStandaloneDisplay() || isFullscreenActive()) {
    try {
      await window.screen?.orientation?.lock?.("landscape");
    } catch {
      /* ignore */
    }
    return true;
  }

  const target = element || document.documentElement;
  let entered = false;

  try {
    if (target.requestFullscreen) {
      await target.requestFullscreen({ navigationUI: "hide" });
      entered = true;
    } else if (target.webkitRequestFullscreen) {
      await Promise.resolve(target.webkitRequestFullscreen());
      entered = true;
    } else if (target.webkitRequestFullScreen) {
      target.webkitRequestFullScreen();
      entered = true;
    } else if (target.mozRequestFullScreen) {
      target.mozRequestFullScreen();
      entered = true;
    } else if (target.msRequestFullscreen) {
      target.msRequestFullscreen();
      entered = true;
    }
  } catch {
    entered = false;
  }

  if (entered) {
    try {
      await window.screen?.orientation?.lock?.("landscape");
    } catch {
      /* ignore */
    }
  }

  return entered;
}

export async function exitImmersiveMode() {
  if (typeof document === "undefined") {
    return;
  }

  try {
    if (isFullscreenActive()) {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  } catch {
    /* ignore */
  }

  try {
    window.screen?.orientation?.unlock?.();
  } catch {
    /* ignore */
  }
}

export function subscribeFullscreenChange(onChange) {
  if (typeof document === "undefined") {
    return () => {};
  }

  const handler = () => onChange(isFullscreenActive());
  const events = [
    "fullscreenchange",
    "webkitfullscreenchange",
    "mozfullscreenchange",
    "MSFullscreenChange",
  ];

  events.forEach((eventName) =>
    document.addEventListener(eventName, handler)
  );

  return () => {
    events.forEach((eventName) =>
      document.removeEventListener(eventName, handler)
    );
  };
}
